import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useLocation } from "react-router-dom";
import Input from "../../components/common/Input";
import Button from "../../components/common/Button";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import { VerifyOTPFormData } from "../../schemas/auth/formData";
import { verifyOTPSchema } from "../../schemas/auth/schemas";
import { Link } from "react-router-dom";

const VerifyOTP: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const { verifyOtp } = useAuth();

  const {
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<VerifyOTPFormData>({
    resolver: zodResolver(verifyOTPSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      otp: "",
    },
  });

  const onSubmit = async (data: VerifyOTPFormData) => {
    if (!email) {
      navigate("/forgot-password");
      return;
    }
    try {
      verifyOtp({ ...data, email });
      navigate("/reset-password", { state: { email } });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to verify OTP");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 shadow-lg rounded bg-white p-10">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-primary-default">
            Verify OTP
          </h2>
          <p className="mt-2 text-center text-sm text-gray-800">
            Please enter the 6-digit OTP sent to your email
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="rounded-md shadow-sm">
            <Input
              label="OTP"
              type="text"
              maxLength={6}
              name="otp"
              value={watch("otp") || ""}
              onChange={(e) => setValue("otp", e.target.value, { shouldValidate: false })}
              onBlur={() => trigger("otp")}
              error={errors.otp}
              required
              className="rounded-t-md"
              placeholder="Enter 6-digit OTP"
            />
          </div>

          <div>
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Verify OTP
            </Button>
          </div>

          <div className="text-sm text-center">
            <Link
              to={"/forgot-password"}
              className="font-medium text-primary-default hover:text-primary-light"
            >
              Back to forgot password
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyOTP;
